import TextInput from 'components/ui/TextInput';
import { FormikProps } from 'formik';
import { FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { backendURL } from 'redux/reducers/settings';
import { handleImageCompress } from 'utils/image-compress';
import { IResourcesFormInput } from './schema';

interface IResourcesFormProps {
  formik: FormikProps<IResourcesFormInput>;
  selectedData?: IResourcesFormInput | null; // For edit mode
  evId: number;
  isloading: boolean;
}

// images larger than this get compressed before upload
const MAX_IMAGE_SIZE_MB = 1;

const formatSize = (bytes: number) => `${(bytes / (1024 * 1024)).toFixed(2)} MB`;

const AddResourcesForm: FC<IResourcesFormProps> = ({ formik, selectedData, evId, isloading }) => {
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState<string>('');

  useEffect(() => {
    if (selectedData) {
      formik.setValues(selectedData);
    } else {
      formik.resetForm();
      formik.setFieldValue('training_id', Number(evId));
    }
    setCompressionInfo('');
  }, [selectedData, evId]);

  const resourceFile: any = formik?.values?.resource_file;
  const isNewFile = resourceFile instanceof File;

  // local preview for a freshly picked image, backend path for an existing one
  const previewUrl = useMemo(() => {
    if (isNewFile) {
      return resourceFile.type?.startsWith('image/') ? URL.createObjectURL(resourceFile) : '';
    }
    return resourceFile ? backendURL + resourceFile : '';
  }, [resourceFile, isNewFile]);

  useEffect(() => {
    return () => {
      if (isNewFile && previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl, isNewFile]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      formik.setFieldValue('resource_file', '');
      setCompressionInfo('');
      return;
    }

    // only images can be compressed, everything else (pdf, doc, ...) uploads as is
    if (!file.type.startsWith('image/')) {
      formik.setFieldValue('resource_file', file);
      setCompressionInfo('');
      return;
    }

    setIsCompressing(true);
    try {
      const compressedFile = await handleImageCompress(file, MAX_IMAGE_SIZE_MB);
      if (compressedFile) {
        formik.setFieldValue('resource_file', compressedFile);
        setCompressionInfo(
          `Compressed ${formatSize(file.size)} → ${formatSize(compressedFile.size)}`
        );
      } else {
        // compression failed, fall back to the original file
        formik.setFieldValue('resource_file', file);
        setCompressionInfo('');
        toast.warn('Could not compress the image, uploading the original file');
      }
    } catch (error) {
      console.error(error);
      formik.setFieldValue('resource_file', file);
      setCompressionInfo('');
      toast.warn('Could not compress the image, uploading the original file');
    } finally {
      setIsCompressing(false);
    }
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="row">
        <TextInput
          label="Name"
          name="name"
          type="text"
          placeholder="Enter the name"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.name}
          formik={formik}
        />

        <div className="col-6">
          Resource
          <div className="">
            <input
              id="profile-img-file-input"
              type="file"
              className="profile-img-file-input"
              name="image"
              disabled={isCompressing}
              onChange={handleFileChange}
            />
            {isCompressing && <div className="text-muted fs-14 mt-1">Compressing image...</div>}
            {!isCompressing && compressionInfo && (
              <div className="text-success fs-14 mt-1">{compressionInfo}</div>
            )}
            {previewUrl && (
              <img
                src={previewUrl}
                style={{ width: '200px', height: '200px', marginTop: '10px' }}
                className="  img-thumbnail "
                alt="resource_file"
              />
            )}
          </div>
        </div>

        {/* Hidden evaluation_id (if needed for submission) */}
        <input type="hidden" name="id" value={formik.values.id} />
      </div>
    </form>
  );
};

export default AddResourcesForm;
